import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { BulkEditController } from '@/features/initiative/bulk-edit/BulkEditController';
import {
  DEFAULT_LIST_STATE,
  parseInitiativeListSearch,
  applyListStateToSearch,
  withFilterChange,
  type InitiativeListState,
  type InitiativeStatus,
} from '@/shared/url/initiative-list-search-params';
import {
  labelLookup,
  useInitiativeList,
  usePlatformOptions,
  useSalesCycleLabels,
} from './initiative-list-data';
import { InitiativeListFilterBar } from './InitiativeListFilterBar';
import { InitiativeListTable } from './InitiativeListTable';

/**
 * The Initiatives screen: a paginated, filtered, identifier-sorted table of every
 * Initiative the requesting user may read.
 *
 * Not year-scoped, unlike the calendar. `GET /api/initiatives` applies no year
 * predicate - `planningYear` is only the frame its month, calendar-week and
 * Sales_Cycle date filters are computed against, and this screen exposes none of
 * those - so a Commercial_Planning_User sees every Initiative here regardless of
 * when it falls. Readability is still the server's: the endpoint reads through the
 * same RLS-scoped base relation as every other surface, so this screen cannot show
 * a row the user could not otherwise read, and its `totalCount` is the readable
 * population rather than the absolute one (R3.3, R11.9).
 *
 * All view state lives in the URL, so a filtered page is linkable and the back
 * button steps through filter changes.
 */
export function InitiativeListPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageChrome({
    title: t('page.initiatives.title'),
    subtitle: t('page.initiatives.subtitle'),
  });

  const state = useMemo(() => parseInitiativeListSearch(searchParams), [searchParams]);

  const session = useSession();
  // R10.3: the Bulk_Edit endpoints are gated on `initiative:write`, so a reader without
  // it gets no checkbox column and no action bar rather than controls that would be
  // refused.
  const canBulkEdit = hasCapability(session.data, 'initiative:write');

  /**
   * The Bulk_Edit selection.
   *
   * Component state, deliberately not the URL. Every other view state on this screen is
   * linkable because it describes *what is shown*; a selection describes what one reader
   * is about to do, and a shared link carrying somebody else's half-made selection would
   * be a trap - they would preview and apply against records they never chose.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  // The year the request states. Inert for the rows this screen shows (see the
  // component doc), so it is the current system year and never a control.
  const planningYear = useMemo(() => new Date().getFullYear(), []);

  const listResult = useInitiativeList(state, planningYear, searchParams);
  const platformsResult = usePlatformOptions();
  const salesCyclesResult = useSalesCycleLabels();

  const commit = useCallback(
    (next: InitiativeListState) => {
      // Preserves every param this screen does not own, so a Drill_Through's
      // carried dimensions and planning year survive a filter change (R6.3, R6.9).
      setSearchParams(applyListStateToSearch(searchParams, next), { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const change = useCallback(
    (partial: Partial<InitiativeListState>) => {
      commit(withFilterChange(state, partial));
    },
    [commit, state],
  );

  const items = listResult.data?.items ?? [];

  /**
   * A filter, sort or page change clears the selection.
   *
   * Keeping it would leave the reader holding records they can no longer see, and a
   * preview over those is a change they cannot check - the rows in front of them would not
   * be the rows about to change. `searchParams.toString()` is the dependency because it is
   * exactly "which rows this screen is showing".
   */
  const viewKey = searchParams.toString();
  useEffect(() => {
    setSelectedIds(new Set());
  }, [viewKey]);
  const totalCount = listResult.data?.totalCount ?? 0;
  const platforms = platformsResult.data ?? [];
  const salesCycleLabel = useMemo(() => labelLookup(salesCyclesResult.data), [salesCyclesResult.data]);

  /**
   * The label reads are part of what makes a row *readable*, so the table waits
   * for them the same way it waits for the page itself.
   *
   * Without this the three reads raced, and whenever the page read won - it is
   * cached per view state, so stepping back to a visited page returns instantly
   * while the reference reads are still in flight - the table painted with an
   * empty Platform lookup and every cell fell back to its raw UUID. That is
   * indistinguishable from corrupt data to anyone reading the screen, and it
   * cleared itself only on the next render, which is why it looked intermittent
   * and why a full page refresh appeared to "fix" it: the refresh either found the
   * Platform records already cached (they are shared with the Calendar_View's
   * filter bar under the same key) or simply lost the race the other way.
   *
   * `isPending` and not `isFetching`: a *background* refresh of already-cached
   * labels must not blank the table, only a first load with nothing to show.
   */
  const labelsPending = platformsResult.isPending || salesCyclesResult.isPending;

  return (
    <div>
      <InitiativeListFilterBar
        statuses={state.statuses}
        platformIds={state.platformIds}
        search={state.search}
        limit={state.limit}
        platforms={platforms}
        resultCount={listResult.isSuccess ? totalCount : null}
        onStatusesChange={(statuses: readonly InitiativeStatus[]) => change({ statuses })}
        onPlatformIdsChange={(platformIds) => change({ platformIds })}
        onSearchChange={(search) => change({ search })}
        onLimitChange={(limit) => change({ limit })}
        onReset={() => commit(DEFAULT_LIST_STATE)}
      />

      {/* The label reads failing is reported rather than swallowed: without them
          the Platform and Sales_Cycle cells can only show an unresolved marker.
          Retrying is offered here because these are independent reads - the page
          itself is fine, so recovering the labels must not cost a page reload. */}
      {(platformsResult.isError || salesCyclesResult.isError) && (
        <Alert variant="default" className="mb-[14px]">
          <AlertTitle>{t('initiativeList.labelsUnavailable.title')}</AlertTitle>
          <AlertDescription>
            {t('initiativeList.labelsUnavailable.description')}
            <span className="mt-2 block">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (platformsResult.isError) void platformsResult.refetch();
                  if (salesCyclesResult.isError) void salesCyclesResult.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      <Card className="gap-4 p-5">
        {listResult.isError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('initiativeList.loadError.title')}</AlertTitle>
            <AlertDescription>{t('initiativeList.loadError.description')}</AlertDescription>
          </Alert>
        ) : listResult.isPending || labelsPending ? (
          <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground text-[12.3px]">{t('initiativeList.empty')}</p>
        ) : (
          <>
            {canBulkEdit ? (
              <BulkEditActionBar
                selectedCount={selectedIds.size}
                onClear={() => setSelectedIds(new Set())}
                onOpen={() => setBulkEditOpen(true)}
              />
            ) : null}
            <InitiativeListTable
              items={items}
              platforms={platforms}
              salesCycleLabel={salesCycleLabel}
              sortDirection={state.sortDirection}
              onToggleSort={() =>
                change({ sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' })
              }
              {...(canBulkEdit
                ? {
                    selection: {
                      selectedIds,
                      onToggle: (initiativeId: string) => {
                        setSelectedIds((current) => {
                          const next = new Set(current);
                          if (next.has(initiativeId)) {
                            next.delete(initiativeId);
                          } else {
                            next.add(initiativeId);
                          }
                          return next;
                        });
                      },
                      onToggleAll: () => {
                        setSelectedIds((current) => {
                          const pageIds = items.map((item) => item.id);
                          const allSelected = pageIds.every((id) => current.has(id));
                          const next = new Set(current);
                          for (const id of pageIds) {
                            if (allSelected) {
                              next.delete(id);
                            } else {
                              next.add(id);
                            }
                          }
                          return next;
                        });
                      },
                    },
                  }
                : {})}
            />
            <Pager
              offset={state.offset}
              limit={state.limit}
              totalCount={totalCount}
              onOffsetChange={(offset) => commit({ ...state, offset })}
            />
          </>
        )}
      </Card>

      {/* The Detail_Panel (`/initiatives/:id`) mounts here without unmounting the
          list, so the filters, sort and page window behind it are untouched. It
          renders into a portal as a modal drawer, so this position in the tree
          carries no layout meaning - only the mount point matters. */}
      <Outlet />

      {/* R10.4, R10.15: preview then apply, in one interaction. */}
      {canBulkEdit ? (
        <BulkEditController
          isOpen={bulkEditOpen}
          selectedIds={selectedIds}
          onClose={() => setBulkEditOpen(false)}
          onApplied={() => {
            setBulkEditOpen(false);
            // The records changed, so the selection describes a state that no longer
            // holds; keeping it would invite a second apply over stale versions.
            setSelectedIds(new Set());
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The selection summary and the Bulk_Edit entry point (R10.3, R10.4).
 *
 * Rendered above the table rather than in the filter bar: it describes what the reader has
 * picked *from* the table, and the filter bar describes what the table shows. It states
 * the count even at zero, so the affordance is discoverable before anything is selected -
 * with the action disabled, because a Bulk_Edit of nothing has nothing to preview.
 */
function BulkEditActionBar({
  selectedCount,
  onClear,
  onOpen,
}: {
  readonly selectedCount: number;
  readonly onClear: () => void;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="border-line bg-surface-alt flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2"
      data-testid="bulk-edit-bar"
      // The count changes as the reader ticks rows, and it is the thing that decides
      // whether the action is available.
      aria-live="polite"
    >
      <span className="text-txt text-[12px]">
        {t('bulkEdit.barSelected', { count: selectedCount })}
      </span>
      <span className="ml-auto flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={selectedCount === 0}
          onClick={onClear}
        >
          {t('bulkEdit.clearSelection')}
        </Button>
        <Button
          type="button"
          size="sm"
          data-testid="bulk-edit-open"
          disabled={selectedCount === 0}
          onClick={onOpen}
        >
          {t('bulkEdit.open')}
        </Button>
      </span>
    </div>
  );
}

/**
 * The page window control.
 *
 * Reads `totalCount`, which the endpoint computes over the unpaged relation, so
 * the range and the "next" affordance describe the whole filtered population
 * rather than the page in hand (R11.9, R13.8).
 */
function Pager({
  offset,
  limit,
  totalCount,
  onOffsetChange,
}: {
  readonly offset: number;
  readonly limit: number;
  readonly totalCount: number;
  readonly onOffsetChange: (offset: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const first = totalCount === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, totalCount);
  const hasPrevious = offset > 0;
  const hasNext = last < totalCount;

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-muted-foreground text-[12px]" aria-live="polite">
        {t('initiativeList.range', { first, last, total: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!hasPrevious}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        >
          {t('initiativeList.previousPage')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!hasNext}
          onClick={() => onOffsetChange(offset + limit)}
        >
          {t('initiativeList.nextPage')}
        </Button>
      </div>
    </div>
  );
}
